import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Bits";
import { money, date, canEdit } from "@/lib/format";
import TaskPicker from "@/components/TaskPicker";
import CustomerPicker from "@/components/CustomerPicker";
import { ParentJobSelect } from "@/components/JobLinks";
import LineItems from "@/components/LineItems";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle } from "lucide-react";

const BILLING = { fixed: "Fixed Price", hourly: "Hourly", per_unit: "Per Unit/Part", mixed: "Mixed" };

export default function Jobs({ completed = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [cfg, setCfg] = useState({ job_types: [], stage_templates: [], staff: [], locations: [] });
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ customer_id: "", parent_job_id: null, customer_po: "", job_type: "", job_name: "", task_names: [], description: "", quantity: 1, due_date: "", location: "Rathcairn", responsible_person: "", quoted_value: 0, estimated_cost: 0, billing_method: "fixed", customer_supplied: false });

  const [filters, setFilters] = useState({ location: "", q: "" });
  const load = useCallback(() => {
    const params = completed ? { status: "completed" } : { active: true };
    if (filters.location) params.location = filters.location;
    api.get("/jobs", { params }).then((r) => setJobs(r.data)).catch(() => {});
  }, [filters.location, completed]);
  const shown = jobs.filter((j) => !filters.q || `${j.job_number} ${j.job_name || ""} ${j.customer_name} ${j.job_type || ""}`.toLowerCase().includes(filters.q.toLowerCase()));
  useEffect(() => { load(); api.get("/config").then((r) => setCfg(r.data)); }, [load]);

  const create = async () => {
    try {
      const payload = { ...f, billing_lines: items.map((l) => ({ description: l.description, qty: Number(l.qty || 0), rate: Number(l.rate || 0) })) };
      const { data } = await api.post("/jobs", payload);
      toast.success(`Created ${data.job_number}`);
      setOpen(false); setItems([]);
      nav(`/jobs/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title={completed ? "Completed Jobs" : "Jobs"} subtitle={completed ? "Jobs the office has marked complete. Open one to reopen it." : "Active jobs. Completed jobs move to Completed Jobs."}>
        {!completed && canEdit(user.role) && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-job-button" className="font-bold uppercase"><Plus className="w-4 h-4 mr-1" />New Job</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading uppercase">Create Job</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Customer *" full><CustomerPicker value={f.customer_id} onChange={(c) => set("customer_id")(c.id)} testid="job-customer" /></Field>
                <Field label="Customer PO"><Input value={f.customer_po} onChange={(e) => set("customer_po")(e.target.value)} data-testid="job-po" /></Field>
                <Field label="Job Type">
                  <Select value={f.job_type} onValueChange={set("job_type")}><SelectTrigger data-testid="job-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{cfg.job_types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent></Select>
                </Field>
                <Field label="Job Name" full><Input value={f.job_name} onChange={(e) => set("job_name")(e.target.value)} placeholder="e.g. Steel columns – Block B" data-testid="job-name" /></Field>
                <Field label="Linked to main job (for costing)" full><ParentJobSelect value={f.parent_job_id} onChange={set("parent_job_id")} testid="job-parent" /></Field>
                <Field label="Job Description" full><Textarea value={f.description} onChange={(e) => set("description")(e.target.value)} rows={2} data-testid="job-desc" /></Field>
                <Field label="Tasks needed on this job *" full><TaskPicker list={cfg.task_list || []} value={f.task_names} onChange={set("task_names")} testid="job-tasks" /></Field>
                <Field label="Quantity"><Input type="number" value={f.quantity} onChange={(e) => set("quantity")(e.target.value)} data-testid="job-qty" /></Field>
                <Field label="Due Date"><Input type="date" value={f.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="job-due" /></Field>
                <Field label="Location">
                  <Select value={f.location} onValueChange={set("location")}><SelectTrigger data-testid="job-location"><SelectValue /></SelectTrigger>
                    <SelectContent>{cfg.locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select>
                </Field>
                <Field label="Responsible">
                  <Select value={f.responsible_person} onValueChange={set("responsible_person")}><SelectTrigger data-testid="job-responsible"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{cfg.staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
                </Field>
                <Field label="Billing Method">
                  <Select value={f.billing_method} onValueChange={set("billing_method")}><SelectTrigger data-testid="job-billing"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(BILLING).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                </Field>
                <Field label="Est. Cost (€)"><Input type="number" value={f.estimated_cost} onChange={(e) => set("estimated_cost")(e.target.value)} data-testid="job-estcost" /></Field>
                <div className="col-span-2"><LineItems lines={items} onChange={setItems} quoted={f.quoted_value} onQuoted={set("quoted_value")} testid="job-items" /></div>
                <Field label="Customer-supplied parts?" full>
                  <Select value={f.customer_supplied ? "yes" : "no"} onValueChange={(v) => set("customer_supplied")(v === "yes")}>
                    <SelectTrigger data-testid="job-custsupplied"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="no">No — MCK supplies materials</SelectItem><SelectItem value="yes">Yes — customer supplies parts</SelectItem></SelectContent></Select>
                </Field>
              </div>
              <DialogFooter><Button onClick={create} disabled={!f.customer_id} data-testid="job-create-submit" className="font-bold uppercase">Create Job</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        <Input placeholder="Search job, client, type…" value={filters.q} onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))} data-testid="jobs-search" className="w-64" />
        <Select value={filters.location || "all"} onValueChange={(v) => setFilters((s) => ({ ...s, location: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40" data-testid="jobs-filter-location"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sites</SelectItem>{cfg.locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {shown.length === 0 ? <Empty text={completed ? "No completed jobs yet." : "No jobs match your filters."} /> : (
        <div className="card-industrial overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Customer / Job</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Billing</th>
                  <th className="text-left px-4 py-3">Progress</th>
                  <th className="text-left px-4 py-3">{completed ? "Completed" : "Due"}</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">Quoted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shown.map((j) => (
                  <tr key={j.id} onClick={() => nav(`/jobs/${j.id}`)} data-testid={`job-row-${j.job_number}`}
                    className="cursor-pointer hover:bg-accent/30 transition-colors">
                    <td className="px-4 py-3 max-w-[280px]"><div className="text-[11px] text-muted-foreground truncate">{j.customer_name}</div><div className="font-semibold truncate" data-testid={`job-name-${j.job_number}`}>{j.job_name || j.job_number}</div><div className="font-mono-num text-[10px] text-muted-foreground">{j.job_number}</div></td>
                    <td className="px-4 py-3 text-muted-foreground">{j.job_type}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{BILLING[j.billing_method]}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-accent overflow-hidden"><div className="h-full bg-primary" style={{ width: `${j.progress_pct}%` }} /></div>
                        <span className="text-xs font-mono-num text-muted-foreground">{j.progress_pct}%</span>
                        {j.stage_blocked > 0 && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {completed ? <span className="text-muted-foreground">{date(j.completed_at)}</span> : <span className={j.is_late ? "text-red-400 font-semibold" : "text-muted-foreground"}>{date(j.due_date)}</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num hidden sm:table-cell">{money(j.quoted_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
